import { useState } from 'react'
import type { Session } from '../auth/useSession'
import { changeOwnPassword } from './useAdminApi'
import { Banner, Btn, Field, PasswordInput, SectionHeader } from './ui'

/** Available to anyone signed in through the local door -- Entra and SSO accounts have no
 *  password here to change. Requires the current password, same as any self-service change;
 *  there is deliberately no admin-reset-somebody-else's-password path (see
 *  auth.change_own_password) -- a locked-out account gets disabled and recreated instead,
 *  which keeps the audit trail honest about who actually picked the new credential. */
export function ProfileSection({ session }: { session: Session }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null)

  const isLocal = session.auth === 'local'

  const submit = async (): Promise<void> => {
    setBanner(null)
    if (next.length < 8) {
      setBanner({ tone: 'error', msg: 'New password must be at least 8 characters' })
      return
    }
    if (next !== confirm) {
      setBanner({ tone: 'error', msg: 'New password and confirmation do not match' })
      return
    }
    setBusy(true)
    try {
      const { reseededNote } = await changeOwnPassword(current, next)
      setBanner({
        tone: 'ok',
        msg: reseededNote ? `Password changed. ${reseededNote}` : 'Password changed.',
      })
      setCurrent(''); setNext(''); setConfirm('')
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Could not change password' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-md space-y-4">
      <SectionHeader
        title="Your account"
        subtitle={`Signed in as ${session.username ?? session.email ?? 'you'}${session.role ? ` · ${session.role.replace('_', ' ')}` : ''}`}
      />

      {!isLocal ? (
        <p className="text-sm text-slate-500">
          You're signed in through {session.auth === 'entra' ? 'Microsoft Entra' : session.auth === 'sso' ? 'single sign-on' : 'this deployment\'s open access'} —
          there's no password here to change.
        </p>
      ) : (
        <>
          {banner ? <Banner tone={banner.tone} onDismiss={() => setBanner(null)}>{banner.msg}</Banner> : null}
          <div className="grid gap-3">
            <Field label="Current password">
              <PasswordInput value={current} onChange={setCurrent} autoComplete="current-password" />
            </Field>
            <Field label="New password">
              <PasswordInput value={next} onChange={setNext} autoComplete="new-password" />
            </Field>
            <Field label="Confirm new password">
              <PasswordInput value={confirm} onChange={setConfirm} autoComplete="new-password" />
            </Field>
          </div>
          <Btn onClick={() => void submit()} disabled={busy || !current || !next || !confirm}>
            {busy ? 'Changing…' : 'Change password'}
          </Btn>
        </>
      )}
    </div>
  )
}
